import { Button, createTheme, ThemeProvider } from '@mui/material';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import TagManager from 'react-gtm-module';
import { useDispatch, useSelector } from 'react-redux';
import { BCCDatePicker } from '../DatePicker/BCCDatePicker';
import { PersonInput } from '../PersonInput/PersonInput';
import { HotelSearchAutocomplete } from './HotelSearchAutocomplete/HotelSearchAutocomplete';
import HotelIcon from '@mui/icons-material/Hotel';
import cn from 'classnames';
import styles from './HotelSearch.module.scss';
import { object, string } from 'yup';
import { useRouter } from 'next/router';
import qs from 'qs';
import { IconButton } from '@material-ui/core';
import CloseIcon from '@mui/icons-material/Close';
import { httpGet } from '../../utils/httpClient';
import { setHotelDestination } from '../../utils/store/features/hotelDestinationSlice';
import { setAutocompleteCache } from '../../utils/store/features/hotelAutocompleteCacheSlice';
import { setHotelDateRange, setHotelVisitors, setAutocomplete } from '../../utils/store/features/hotelSearchSlice';
import { saveHotelSearch } from '../../utils/userSearchStorage';

const theme = createTheme({
  shape: {
    borderRadius: 12,
  },
  palette: {
    primary: {
      main: '#7E183D',
    },
    dark: {
      main: '#0a0a0a',
    },
  },
  typography: {
    fontFamily: '"Libre Franklin", sans-serif',
  },
});
const HotelSearch = ({
  viewMode,
  value,
  collapsed,
  toggleSearch,
  hotelName,
  hotelId,
}) => {
  const router = useRouter();
  const userStore = useSelector((state) => state.user?.value);
  const dispatch = useDispatch();
  const hotelAutocompleteCacheStore = useSelector(
    (state) => state.hotelAutocompleteCache?.value
  );
  const hotelSearchStore = useSelector(
    (state) => state.hotelSearch
  );
  const hotelAutocompleteStore = hotelSearchStore?.autocomplete;
  const hotelDateRangeStore = hotelSearchStore?.dateRange;
  const hotelVisitorsStore = hotelSearchStore?.visitors;
  const [searchSchema, setSearchSchema] = useState(
    object().shape({
      autocomplete: object()
        .nullable()
        .required('Please select destination'),
      dateRange: object().shape({
        departure: string().nullable(),
        return: string().nullable(),
      }),
    })
  );
  const [initialValues, setInitialValues] = useState({
    autocomplete: hotelAutocompleteStore,
    dateRange: {
      departure: hotelDateRangeStore?.departure || null,
      return: hotelDateRangeStore?.return || null,
    },
    visitors: hotelVisitorsStore || null,
  });
  useEffect(() => {
    setInitialValues({
      autocomplete:
        hotelAutocompleteStore ||
        initialValues.autocomplete,
      dateRange: {
        departure:
          hotelDateRangeStore?.departure ||
          initialValues.dateRange.departure
        ,
        return:
          hotelDateRangeStore?.return ||
          initialValues.dateRange.return
        ,
      },
      visitors: hotelVisitorsStore || initialValues.visitors
    });
  }, [value, hotelDateRangeStore, hotelVisitorsStore, hotelAutocompleteStore]);

  useEffect(() => {
    if (value) {
      if (value.visitors?.length) {
        value.visitors.forEach((visitor) => {
          if (!visitor.childrenAges) {
            visitor.childrenAges = [];
          }
        });
      }
      if (!hotelVisitorsStore || hotelVisitorsStore.length === 0) {
        dispatch(setHotelVisitors(value.visitors || [{ adults: 1, childrenAges: [] }]));
      }
    }
  }, [value]);

  useEffect(() => {
    if (hotelName) {
      if (!hotelAutocompleteCacheStore[hotelName]) {
        httpGet(`/api/hotel/autocomplete?search=${hotelName}`).then(
          (response) => {
            response.json().then((result) => {
              if (
                result?.length &&
                result.find((hotel) => hotel.n === hotelName)
              ) {
                setInitialValues({
                  ...initialValues,
                  autocomplete: result.find((hotel) => hotel.n === hotelName && hotel.k === hotelId),
                });
                dispatch(
                  setAutocompleteCache({ key: hotelName, value: result })
                );
              } else {
                const autocomplete = {
                  n: hotelName,
                  t: 'hotels',
                  s: 1,
                  empty: true,
                };
                setInitialValues({
                  ...initialValues,
                  autocomplete,
                });
                dispatch(
                  setAutocompleteCache({
                    key: hotelName,
                    value: [autocomplete],
                  })
                );
              }
            });
          }
        );
      }
    }
  }, [hotelName]);
  useEffect(() => {
    if (initialValues?.autocomplete) {
      dispatch(setHotelDestination(initialValues.autocomplete));
    }
  }, [initialValues]);

  // Initialize Redux store with date range if it's empty and component has values
  useEffect(() => {
    if (value?.dateRange?.departure || value?.dateRange?.return) {
      if (!hotelDateRangeStore?.departure && !hotelDateRangeStore?.return) {
        dispatch(setHotelDateRange({
          departure: value.dateRange.departure,
          return: value.dateRange.return
        }));
      }
    }
  }, []);

  // Initialize Redux store with visitors if it's empty and component has values
  useEffect(() => {
    if (value?.visitors?.length) {
      if (!hotelVisitorsStore || hotelVisitorsStore.length === 0) {
        dispatch(setHotelVisitors(value.visitors));
      }
    }
  }, []);

  // Initialize Redux store with visitors if it's empty and component has values
  useEffect(() => {
    if (value?.autocomplete) {
      if (!hotelAutocompleteStore) {
        dispatch(setAutocomplete(value.autocomplete));
      }
    }
  }, []);

  const toggleCollapsedSearch = (event) => {
    event.preventDefault();
    toggleSearch();
  };

  const onSubmit = (values) => {
    dispatch(setHotelDestination(values.autocomplete));
    const destination = values.autocomplete
    ? values.autocomplete.t === 'cities' ||
      values.autocomplete.t === 'countries'
      ? values.autocomplete.n
      : values.autocomplete?.p?.[0]
    : 'directly from hotel page';
    const guestsCount = {
      numGuests: 0,
      numAdults: 0,
      numChildren: 0,
    };
    values.visitors.forEach(v => {
      const guests = +v.adults + (v.childrenAges?.length || 0);
      guestsCount.numGuests += guests;
      guestsCount.numAdults += +v.adults;
      guestsCount.numChildren += v.childrenAges?.length || 0;
    });

    const tagManagerArgs = {
      dataLayer: {
        event: 'hotelSearch',
        destinationCountry:
          values.autocomplete?.cc || 'directly from hotel page',
        destinationCity: destination,
        numGuests: values.visitors.reduce((acc = 0, v) => {
          acc +=
            v.childrenAges?.length > 0
              ? +v.adults + v.childrenAges?.length
              : +v.adults;
          return acc;
        }, 0),
        numAdults: values.visitors.reduce((acc = 0, v) => {
          acc += +v.adults;
          return acc;
        }, 0),
        numChildren: values.visitors.reduce((acc = 0, v) => {
          acc += v.childrenAges?.length;
          return acc;
        }, 0),
        userCountry: userStore?.country.toUpperCase(),
        pageType: router?.pathname,
        loginStatus: userStore.userid ? 'logged in' : 'not logged in',
        membershipId: userStore?.membershipNumber,
        traffic_type: userStore?.is_admin ? 'internal' : 'external',
      },
    };

    TagManager.dataLayer(tagManagerArgs);

    saveHotelSearch({
      destination: destination,
      destinationCountry: values.autocomplete?.cc,
      guestsCount: guestsCount,
      departure: values.dateRange?.departure,
      return: values.dateRange?.return,
      rooms: values.visitors.length
    });

    if (values.autocomplete?.t === 'hotels' || hotelId) {
      if (router.route === '/hotel/review/[id]' && hotelId) {
        router.push(
          decodeURI(`/hotel/review/${hotelId}?${qs.stringify(values)}`)
        );
      } else {
        router.push(
          decodeURI(
            `/hotel/review/${values.autocomplete?.k.replace(
              `khotel:`,
              'hc-'
            )}?${qs.stringify(values)}`
          )
        );
      }
    } else {
      values.search = 1;
      router.push(decodeURI(`/hotelsearch?${qs.stringify(values)}`));
    }
  };
  return (
    <ThemeProvider theme={theme}>
      <div className="lg:w-full">
        {collapsed && (
          <div className="px-4 text-center pt-7 font-garamond relative">
            <IconButton
              color="inherit"
              onClick={toggleCollapsedSearch}
              aria-label="close"
              className="absolute top-0 right-0"
            >
              <CloseIcon />
            </IconButton>
          </div>
        )}
      </div>
      <div className={cn(styles.hotelSearchContainer)}>
        <Formik
          enableReinitialize
          validationSchema={searchSchema}
          validate={(values) => {
            const schemaShape = {}
            if (values.dateRange.departure || values.dateRange.return || hotelId) {
              // Search hotel offers (or offers for current hotel)
              schemaShape.dateRange = object().shape({
                departure: string()
                  .nullable()
                  .required('Please select Check-in date'),
                return: string()
                  .nullable()
                  .required('Please select Check-out date'),
              })
            } else {
              // Search just hotels without offers (search without dates)
              schemaShape.dateRange = object().shape({
                departure: string()
                  .nullable(),
                return: string()
                  .nullable(),
              })
            }
            if (!hotelId) {
              // We don't need autocomplete if already in hotel
              schemaShape.autocomplete = object()
                .nullable()
                .required('Please select destination')
            }
            setSearchSchema(object().shape(schemaShape));
          }}
          initialValues={initialValues}
          onSubmit={onSubmit}
        >
          {({ setFieldValue, values, errors, touched }) => {
            return (
              <Form>
                <div
                  className={cn({
                    [styles.formContainer]: viewMode === 'mainSearch',
                    [styles.formContainerSide]: viewMode === 'sideSearch',
                  })}
                >
                  <div className={cn(styles.hotelPersonInput)}>
                    <div className={cn(styles.hotelPersonInputWrapper)}>
                      <PersonInput
                        name="visitors"
                        setFieldValue={(name, value) => {
                          setFieldValue(name, value);
                          // Sync visitors with Redux store
                          if (name === 'visitors') {
                            dispatch(setHotelVisitors(value));
                          }
                        }}
                        disablePortal={false}
                        multiple
                        multipleValue={values?.visitors}
                        multiModeHeading="Room"
                        primaryColor="#7E183D"
                      />
                    </div>
                  </div>
                  {!hotelId && (
                    <div className={cn(styles.hotelAutocomplete)}>
                      <HotelSearchAutocomplete
                        setFieldValue={(name, value) => {
                          setFieldValue(name, value);
                          // Sync autocomplete with Redux store
                          if (name === 'autocomplete') {
                            dispatch(setAutocomplete(value));
                          }
                        }}
                        name="autocomplete"
                        label="Hotel"
                        placeholder="Enter destination or hotel name"
                        icon={<HotelIcon/>}
                        error={!!(touched.autocomplete && errors.autocomplete)}
                        primaryColor="#7E183D"
                        value={values.autocomplete}
                      />
                      {touched.autocomplete && errors.autocomplete && (
                        <div className={styles.errorMessage}>
                          {errors.autocomplete}
                        </div>
                      )}
                    </div>
                  )}
                  <div className={cn(styles.hotelDatePicker, 'mb-3')}>
                    <BCCDatePicker
                      mode="range"
                      labelStart="Check-in"
                      labelEnd="Check-out"
                      setFieldValue={(name, value) => {
                        setFieldValue(name, value);
                        // Sync date range with Redux store
                        if (name === 'dateRange') {
                          dispatch(setHotelDateRange(value));
                        }
                      }}
                      name="dateRange"
                      allowSameDateSelection={false}
                      primaryColor="#7E183D"
                      maxRange={30}
                      viewMode={
                        viewMode === 'sideSearch' ? 'vertical' : 'horizontal'
                      }
                      rangeValue={[
                        values.dateRange.departure,
                        values.dateRange.return,
                      ]}
                      error={
                        !!(touched && touched.dateRange && errors.dateRange)
                      }
                      errorMessages={
                        touched &&
                        touched.dateRange &&
                        errors &&
                        errors.dateRange
                      }
                    />
                  </div>

                  <div className={cn(styles.submit)}>
                    {viewMode === 'mainSearch' && (
                      <Button
                        fullWidth
                        color="primary"
                        variant="contained"
                        type="submit"
                        sx={{
                          color: '#ffffff',
                          background: '#7E183D',
                          textTransform: 'unset',
                          fontSize: '15px',
                          lineHeight: '24px',
                        }}
                      >
                        Search
                      </Button>
                    )}
                    {viewMode !== 'mainSearch' && (
                      <Button
                        fullWidth
                        color="dark"
                        variant="contained"
                        type="submit"
                        sx={{
                          color: '#ffffff',
                          background: '#7E183D',
                          textTransform: 'unset',
                          fontSize: '15px',
                          lineHeight: '24px',
                        }}
                      >
                        Search
                      </Button>
                    )}
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </ThemeProvider>
  );
};

export default HotelSearch;
